import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * The single client read of the launch offer.
 *
 * Every "Included during launch" surface derives from this one payload, so flipping
 * `payments_enabled` in entitlement settings changes signup, the Plans page and the
 * lock-in action at the same moment. This hook never decides access — capabilities
 * still come from useEntitlements.
 */

export type LaunchOffer = {
  /** True once real billing is switched on. */
  payments_enabled: boolean;
  /** True while nobody can be charged — the launch period. */
  launch_active: boolean;
  joined_during_launch: boolean;
  plan_id_at_signup: string | null;
  /** Set when the company committed to launch pricing (no card is taken yet). */
  pricing_locked_at: string | null;
};

const empty: LaunchOffer = {
  payments_enabled: false,
  launch_active: true,
  joined_during_launch: false,
  plan_id_at_signup: null,
  pricing_locked_at: null,
};

export function useLaunchOffer() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['launch-offer', user?.id],
    queryFn: async (): Promise<LaunchOffer> => {
      const { data, error } = await supabase.rpc('get_launch_offer');
      if (error) throw error;
      return (data as unknown as LaunchOffer) ?? empty;
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  /** Records the launch commitment for the caller's company. No card is collected. */
  const lockPricing = async (lock = true, planId?: string | null) => {
    const { error } = await supabase.rpc('record_launch_access', {
      _plan_id: planId ?? null,
      _lock: lock,
    });
    if (error) throw error;
    await queryClient.invalidateQueries({ queryKey: ['launch-offer', user?.id] });
  };

  return {
    offer: data ?? empty,
    isLoading,
    isError: !!error,
    refetch,
    lockPricing,
  };
}

/** Copy used wherever launch access is explained. One source, so it can't drift. */

/** The Buyer plan is free permanently — never phrased as a launch promotion. */
export const FREE_FOREVER_LABEL = 'Free forever';

/** The one sentence people should read first. Plain, no insider vocabulary. */
export const LAUNCH_HEADLINE =
  "Launching soon — every plan is free until then. After launch, new sign-ups get their first 30 days free."
export const LAUNCH_NOTICE = 'No card needed now — switch plans anytime.';
export const LAUNCH_BADGE_LABEL = 'Free right now';
export const LAUNCH_LOCK_LABEL = 'Lock in this price';
export const LAUNCH_LOCK_REWARD =
  'Hold this price for 12 months and get an extra free month when billing starts. No card now.';
/** Signup includes the lock automatically — shown instead of an opt-in checkbox. */
export const LAUNCH_LOCK_INCLUDED =
  'Signing up now also holds your price for 12 months, plus an extra free month when billing starts.';

/* Live billing copy. The public offer is 30 days; the 60-day trial exists only on
   partner links and must never appear on a public page. */
export const TRIAL_HEADLINE =
  'All paid plans include a 30-day free trial. Buyer accounts are free forever.';
export const TRIAL_NOTICE = 'Switch or cancel anytime.';
export const TRIAL_BADGE_LABEL = '30-day free trial';
export const TRIAL_CTA_LABEL = 'Start 30-day free trial';
